import { NET, PISTOL, lerp, lerpAngle } from "@tiny-seas/shared";

/**
 * Snapshot interpolation on the server's tick timeline. Each patch carries
 * `state.tick`; the client renders `NET.interpolationDelay` behind its
 * estimate of the newest server time, so clock jitter never leaks in.
 */

const TICK = 1 / NET.tickRate;

export class ServerClock {
  private offset: number | undefined;

  /** Record a patch that carries `tick`, received at local time `now` (s). */
  observe(tick: number, now: number): void {
    const sample = now - tick * TICK;
    if (this.offset === undefined || sample < this.offset) this.offset = sample;
    // Drift slowly upward so a single early packet doesn't pin the estimate.
    else this.offset += (sample - this.offset) * 0.01;
  }

  reset(): void {
    this.offset = undefined;
  }

  /** Local time (s) of server tick 0 by the min-offset estimate; undefined before the first patch. */
  get origin(): number | undefined {
    return this.offset;
  }

  /** Server time (in ticks) to render at. */
  renderTick(now: number): number {
    if (this.offset === undefined) return 0;
    return (now - this.offset - NET.interpolationDelay) / TICK;
  }
}

export interface PoseSample {
  tick: number;
  /** Frame of the coordinates (0 world, 1 ship-local); ships always use 0. */
  frame: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  mode: number;
  station: string;
  cannonLoaded: boolean;
  pitch: number;
  launchSerial: number;
  /** Discontinuity marker (respawn, teleport): never interpolate across a change. */
  epoch: number;
  // Step-held action state, named as in PlayerState and held from `actionTick`.
  equipment: string;
  bucketFull: boolean;
  action: string;
  carrying: string;
  actionProgress: number;
  recoverIn: number;
  attackRemaining: number;
  attackDuration: number;
  attackStep: number;
  attackSerial: number;
  staggerRemaining: number;
  pistolReloadRemaining: number;
  pistolRounds: number;
  pistolReserve: number;
  slideRemaining: number;
  slideYaw: number;
  vy: number;
  digTick: number;
  blocking: boolean;
  scopeRaised: boolean;
  pistolAiming: boolean;
  crouched: boolean;
  sprinting: boolean;
  grounded: boolean;
  /** Server tick the step-held fields came from; countdowns age from it. */
  actionTick: number;
}

/** Fields `push()` copies from the replicated state, besides the pose itself. */
export type PosePresentation = Omit<PoseSample, "tick" | "frame" | "x" | "y" | "z" | "yaw" | "epoch" | "actionTick">;

export function createPoseSample(): PoseSample {
  return {
    tick: 0, frame: 0, x: 0, y: 0, z: 0, yaw: 0, epoch: 0,
    mode: 0, station: "", cannonLoaded: false, pitch: 0, launchSerial: 0,
    equipment: "shovel", bucketFull: false, action: "", carrying: "", actionProgress: 0, recoverIn: 0,
    attackRemaining: 0, attackDuration: 0, attackStep: 0, attackSerial: 0, staggerRemaining: 0,
    pistolReloadRemaining: 0, pistolRounds: PISTOL.capacity, pistolReserve: PISTOL.maxAmmo - PISTOL.capacity,
    slideRemaining: 0, slideYaw: 0, vy: 0, digTick: -1,
    blocking: false, scopeRaised: false, pistolAiming: false, crouched: false, sprinting: false, grounded: true,
    actionTick: 0,
  };
}

const DEFAULT_PRESENTATION: Readonly<PosePresentation> = createPoseSample();

/** Copies every presentation field (and `actionTick` when `from` is a sample). */
function copyPresentation(from: Readonly<PosePresentation>, out: PoseSample): void {
  out.mode = from.mode;
  out.station = from.station;
  out.cannonLoaded = from.cannonLoaded;
  out.pitch = from.pitch;
  out.launchSerial = from.launchSerial;
  out.equipment = from.equipment;
  out.bucketFull = from.bucketFull;
  out.action = from.action;
  out.carrying = from.carrying;
  out.actionProgress = from.actionProgress;
  out.recoverIn = from.recoverIn;
  out.attackRemaining = from.attackRemaining;
  out.attackDuration = from.attackDuration;
  out.attackStep = from.attackStep;
  out.attackSerial = from.attackSerial;
  out.staggerRemaining = from.staggerRemaining;
  out.pistolReloadRemaining = from.pistolReloadRemaining;
  out.pistolRounds = from.pistolRounds;
  out.pistolReserve = from.pistolReserve;
  out.slideRemaining = from.slideRemaining;
  out.slideYaw = from.slideYaw;
  out.vy = from.vy;
  out.digTick = from.digTick;
  out.blocking = from.blocking;
  out.scopeRaised = from.scopeRaised;
  out.pistolAiming = from.pistolAiming;
  out.crouched = from.crouched;
  out.sprinting = from.sprinting;
  out.grounded = from.grounded;
}

const CAPACITY = 32;

/** Fixed-size ring of samples; `sample()` writes into a caller-owned object. */
export class SnapshotBuffer {
  private readonly items: PoseSample[] = [];
  private head = 0;
  private count = 0;

  constructor() {
    for (let i = 0; i < CAPACITY; i++) this.items.push(createPoseSample());
  }

  push(
    tick: number, frame: number, x: number, y: number, z: number, yaw: number, epoch: number,
    presentation?: Readonly<PosePresentation>,
  ): void {
    const last = this.count > 0 ? this.items[(this.head + this.count - 1) % CAPACITY]! : undefined;
    if (last && tick < last.tick) return;
    let slot: PoseSample;
    if (last && tick === last.tick) {
      // Same tick re-sent (no simulation step in between): refresh all fields in place.
      slot = last;
    } else if (this.count < CAPACITY) {
      slot = this.items[(this.head + this.count) % CAPACITY]!;
      this.count++;
    } else {
      slot = this.items[this.head]!;
      this.head = (this.head + 1) % CAPACITY;
    }
    slot.tick = tick;
    slot.frame = frame;
    slot.x = x;
    slot.y = y;
    slot.z = z;
    slot.yaw = yaw;
    slot.epoch = epoch;
    copyPresentation(presentation ?? DEFAULT_PRESENTATION, slot);
    slot.actionTick = tick;
  }

  clear(): void {
    this.count = 0;
    this.head = 0;
  }

  get size(): number {
    return this.count;
  }

  latest(): PoseSample | undefined {
    return this.count > 0 ? this.items[(this.head + this.count - 1) % CAPACITY] : undefined;
  }

  /** Interpolated pose at `tick`; holds the newest sample when rendering ahead of data. */
  sample(tick: number, out: PoseSample): boolean {
    if (this.count === 0) return false;
    let prev = this.items[this.head]!;
    if (tick <= prev.tick) {
      Object.assign(out, prev);
      return true;
    }
    for (let i = 1; i < this.count; i++) {
      const next = this.items[(this.head + i) % CAPACITY]!;
      if (tick <= next.tick) {
        if (tick === next.tick) {
          Object.assign(out, next);
          return true;
        }
        // Containment and accepted launches switch on their patch tick, even
        // when the same patch also changes frame or marks a discontinuity.
        if (prev.cannonLoaded !== next.cannonLoaded || prev.launchSerial !== next.launchSerial) {
          Object.assign(out, prev);
          return true;
        }
        if (prev.frame !== next.frame || prev.epoch !== next.epoch) {
          Object.assign(out, next);
          copyPresentation(prev, out);
          out.actionTick = prev.actionTick;
          return true;
        }
        const t = (tick - prev.tick) / (next.tick - prev.tick);
        out.tick = tick;
        out.frame = next.frame;
        out.epoch = next.epoch;
        out.x = lerp(prev.x, next.x, t);
        out.y = lerp(prev.y, next.y, t);
        out.z = lerp(prev.z, next.z, t);
        out.yaw = lerpAngle(prev.yaw, next.yaw, t);
        copyPresentation(prev, out);
        out.actionTick = prev.actionTick;
        out.pitch = prev.mode === next.mode && prev.station === next.station
          ? lerp(prev.pitch, next.pitch, t)
          : prev.pitch;
        return true;
      }
      prev = next;
    }
    Object.assign(out, prev);
    return true;
  }
}
